import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import { schemaColumnsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import '../AdminDashboard/styles.css';
import './styles.css';

/**
 * @typedef {Object} Schema
 * @property {string} schema_id
 * @property {string} table_name
 * @property {number} version
 * @property {boolean=} is_org_template
 */

/**
 * @typedef {Object} SchemaColumn
 * @property {string} schema_col_id
 * @property {string} schema_id
 * @property {string=} schema_name
 * @property {string=} entity_name
 * @property {string} original_column_name
 * @property {string} normalized_column_name
 * @property {string=} created_at
 * @property {string=} data_type
 * @property {boolean=} is_required
 * @property {number=} column_order
 * @property {number=} version
 */

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function toSafeFilename(value) {
  const raw = String(value || '').trim() || 'schema';
  return raw.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * @typedef {Object} ImportError
 * @property {number} row
 * @property {string|null} schema_col_id
 * @property {string} error
 */

/**
 * @typedef {Object} ImportSummary
 * @property {boolean} success
 * @property {number} updated
 * @property {number} skipped
 * @property {ImportError[]} errors
 */

function Spinner({ label = 'Loading…', size = 16 }) {
  return (
    <span className="fields-spinner" aria-label={label} role="status">
      <span className="fields-spinner__dot" style={{ width: size, height: size }} />
      <span className="fields-spinner__label">{label}</span>
    </span>
  );
}

function InlineEditableCell({
  value,
  placeholder,
  disabled,
  saving,
  tooltip,
  onCommit
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = async () => {
    const next = String(draft || '').trim();
    const prev = String(value || '').trim();
    setEditing(false);
    if (!next || next === prev) {
      setDraft(value || '');
      return;
    }
    await onCommit(next);
  };

  if (editing) {
    return (
      <div className="fields-edit-input-wrap">
        <input
          ref={inputRef}
          className="fields-edit-input"
          value={draft}
          disabled={!!saving}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            commit().catch(() => {});
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit().catch(() => {});
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
              setDraft(value || '');
            }
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`fields-editable-cell${disabled ? ' is-disabled' : ''}`}
      title={tooltip || ''}
      onClick={() => {
        if (disabled) return;
        setEditing(true);
      }}
    >
      <span className="fields-editable-text">{value || <span className="fields-muted">{placeholder}</span>}</span>
      {saving ? <span className="fields-inline-saving">Saving…</span> : null}
    </button>
  );
}

function UploadSchemaColumnsModal({
  open,
  schemaName,
  disabled,
  loading,
  summary,
  onClose,
  onUpload
}) {
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
    }
  }, [open]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open && !loading) onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [open, loading, onClose]);

  const validateFile = (f) => {
    if (!f) return 'Please choose a file';
    const name = String(f.name || '').toLowerCase();
    const ok = name.endsWith('.xlsx') || name.endsWith('.csv');
    return ok ? null : 'Only .xlsx or .csv files are allowed';
  };

  const onPickFile = (f) => {
    const err = validateFile(f);
    if (err) return { ok: false, error: err };
    setFile(f);
    return { ok: true, error: null };
  };

  if (!open) return null;

  return (
    <div className="admin-modal-overlay" onClick={() => !loading && onClose()}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="admin-modal-header">
          <div>Upload Mapping File{schemaName ? ` — ${schemaName}` : ''}</div>
          <button className="admin-btn admin-btn-secondary" onClick={() => !loading && onClose()}>
            ✕
          </button>
        </div>

        <div className="admin-modal-body">
          {/* <div className="fields-upload-hint">
            Upload an Excel/CSV with columns: <span className="fields-mono">schema_col_id</span> and{' '}
            <span className="fields-mono">normalized_column_name</span>.
          </div> */}

          <div
            className={`fields-dropzone${disabled ? ' is-disabled' : ''}`}
            onDragOver={(e) => {
              if (disabled) return;
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              if (disabled) return;
              e.preventDefault();
              e.stopPropagation();
              const f = e.dataTransfer?.files?.[0];
              onPickFile(f);
            }}
          >
            <div className="fields-dropzone-title">Drag & drop a file here</div>
            <div className="fields-dropzone-subtitle">or</div>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              disabled={disabled || loading}
              onClick={() => inputRef.current?.click()}
            >
              Choose file
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                onPickFile(f);
              }}
            />
            {file ? (
              <div className="fields-selected-file">
                <div className="fields-selected-file__name">{file.name}</div>
                <div className="fields-selected-file__meta">{Math.round(file.size / 1024)} KB</div>
              </div>
            ) : (
              <div className="fields-selected-file fields-selected-file--empty">No file selected</div>
            )}
          </div>

          {/* {summary ? (
            <div className="fields-import-summary">
              <div className="fields-import-summary__row">
                <div className="fields-import-summary__k">Updated</div>
                <div className="fields-import-summary__v">{summary.updated}</div>
              </div>
              <div className="fields-import-summary__row">
                <div className="fields-import-summary__k">Skipped</div>
                <div className="fields-import-summary__v">{summary.skipped}</div>
              </div>
              {Array.isArray(summary.errors) && summary.errors.length > 0 ? (
                <div className="fields-import-errors">
                  <div className="fields-import-errors__title">Errors</div>
                  <div className="fields-import-errors__list">
                    {summary.errors.slice(0, 50).map((err, idx) => (
                      <div key={`${err.row}-${idx}`} className="fields-import-errors__item">
                        <span className="fields-mono">Row {err.row}</span>
                        <span className="fields-muted">
                          {err.schema_col_id ? ` — ${err.schema_col_id}` : ''}
                        </span>
                        <span className="fields-import-errors__msg">{err.error}</span>
                      </div>
                    ))}
                    {summary.errors.length > 50 ? (
                      <div className="fields-muted">Showing first 50 errors.</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null} */}
        </div>

        <div className="admin-modal-actions">
          <button className="admin-btn admin-btn-secondary" disabled={loading} onClick={() => !loading && onClose()}>
            Close
          </button>
          <button
            className="admin-btn admin-btn-primary"
            disabled={disabled || loading || !file}
            onClick={async () => {
              if (!file) return;
              const err = validateFile(file);
              if (err) return;
              await onUpload(file);
            }}
          >
            {loading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const AdminFieldsPanel = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  /** @type {[Schema[], Function]} */
  const [schemas, setSchemas] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(true);
  const [schemasError, setSchemasError] = useState('');

  // Default to "all schemas"
  const [schemaId, setSchemaId] = useState('all');

  /** @type {[SchemaColumn[], Function]} */
  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState('');

  const [exporting, setExporting] = useState(false);

  const [savingById, setSavingById] = useState(() => ({}));

  const [uploadOpen, setUploadOpen] = useState(false);
  /** @type {[ImportSummary|null, Function]} */
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isAllSchemas = schemaId === 'all';

  const selectedSchema = useMemo(() => {
    const list = Array.isArray(schemas) ? schemas : [];
    return list.find((s) => String(s.schema_id) === String(schemaId)) || null;
  }, [schemas, schemaId]);

  const schemaLabel = useMemo(() => {
    if (isAllSchemas) return 'All schemas';
    if (!selectedSchema) return '';
    const name = selectedSchema.table_name || 'Schema';
    const version = selectedSchema.version != null ? ` v${selectedSchema.version}` : '';
    return `${name}${version}`;
  }, [selectedSchema, isAllSchemas]);

  const loadSchemas = useCallback(async () => {
    setSchemasLoading(true);
    setSchemasError('');
    try {
      const list = await schemaColumnsService.listSchemas();
      setSchemas(Array.isArray(list) ? list : []);
      // Preserve selection if still exists, else default to "all"
      setSchemaId((prev) => {
        const next = String(prev || '');
        if (next === 'all') return 'all';
        if (next && list.some((s) => String(s.schema_id) === next)) return next;
        return 'all';
      });
    } catch (e) {
      setSchemasError(e.message || 'Failed to load schemas');
    } finally {
      setSchemasLoading(false);
    }
  }, []);

  const loadColumns = useCallback(
    async (id) => {
      const sid = String(id || '');
      if (!sid) {
        setColumns([]);
        setColumnsError('');
        return;
      }
      setColumnsLoading(true);
      setColumnsError('');
      try {
        const list = sid === 'all' ? await schemaColumnsService.listAll() : await schemaColumnsService.list(sid);
        setColumns(Array.isArray(list) ? list : []);
      } catch (e) {
        setColumnsError(e.message || 'Failed to load schema columns');
      } finally {
        setColumnsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSchemas().catch(() => {});
  }, [loadSchemas]);

  useEffect(() => {
    loadColumns(schemaId).catch(() => {});
  }, [schemaId, loadColumns]);

  const canAct = !!schemaId && !schemasLoading && schemaId !== 'all';

  return (
    <div className={`fields-page${embedded ? ' fields-page--embedded' : ''}`}>
      {!embedded ? (
        <div className="fields-header">
          <button onClick={() => navigate('/admin-dashboard')} className="admin-back-btn">
            Back
          </button>
          <div>
            <h1 className="fields-title">Fields</h1>
            <div className="fields-subtitle">Manage normalized field names for each schema.</div>
          </div>
        </div>
      ) : (
        <div className="fields-embedded-title">
          <h3>Fields</h3>
          <span className="admin-pill-muted" title="Only normalized name is editable">
            Only normalized name is editable
          </span>
        </div>
      )}

      <div className="fields-toolbar">
        <div className="fields-selector">
          <div className="fields-selector__label">Schema</div>
          <div className="fields-selector__control">
            <select
              className="admin-select"
              value={schemaId}
              disabled={schemasLoading}
              onChange={(e) => setSchemaId(e.target.value)}
            >
              <option value="all">All schemas</option>
              {schemas.map((s) => (
                <option key={s.schema_id} value={s.schema_id}>
                  {(s.table_name || 'Schema') + (s.version != null ? ` (v${s.version})` : '')}
                </option>
              ))}
            </select>
            {schemasLoading ? <Spinner label="Loading schemas…" /> : null}
          </div>
          {schemasError ? <div className="fields-error">{schemasError}</div> : null}
        </div>

        <div className="fields-actions">
          {/* <button
            type="button"
            className="admin-btn admin-btn-secondary"
            disabled={!canAct || syncing}
            onClick={async () => {
              if (!schemaId) return;
              try {
                setSyncing(true);
                const res = await schemaColumnsService.sync(schemaId);
                const inserted = res?.inserted ?? res?.data?.inserted;
                showSuccess(
                  typeof inserted === 'number'
                    ? `Fetched latest columns (${inserted} inserted)`
                    : 'Fetched latest columns'
                );
                await loadColumns(schemaId);
              } catch (e) {
                showError(e.message || 'Failed to fetch latest');
              } finally {
                setSyncing(false);
              }
            }}
            title={!schemaId ? 'Select a schema first' : 'Sync columns from schema definition'}
          >
            {syncing ? 'Fetching…' : 'Fetch Latest'}
          </button> */}

          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            disabled={!canAct || exporting}
            onClick={async () => {
              if (!schemaId) return;
              try {
                setExporting(true);
                const res = await schemaColumnsService.exportXlsx(schemaId);
                const filename = `${toSafeFilename(selectedSchema?.table_name || 'schema')}_schema.xlsx`;
                const url = window.URL.createObjectURL(res.blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Export started');
              } catch (e) {
                showError(e.message || 'Failed to export');
              } finally {
                setExporting(false);
              }
            }}
            title={!canAct ? 'Select a schema to export' : 'Download schema column mappings'}
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>

          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={!canAct}
            onClick={() => {
              setUploadSummary(null);
              setUploadOpen(true);
            }}
            title={!schemaId ? 'Select a schema first' : 'Upload a mapping file (.xlsx/.csv)'}
          >
            Upload File
          </button>
        </div>
      </div>

      <div className="fields-grid-card">
        <div className="fields-grid-head">
          <div className="fields-grid-title">
            <div className="fields-grid-title__name">{schemaLabel || 'Schema columns'}</div>
            <div className="fields-grid-title__hint" title="Only normalized name is editable">
              Only normalized name is editable
            </div>
          </div>
          <div className="fields-grid-meta">
            {columnsLoading ? <Spinner label="Loading columns…" /> : null}
            {!columnsLoading ? (
              <span className="fields-count-pill">{columns.length} columns</span>
            ) : (
              <span className="fields-count-pill">—</span>
            )}
          </div>
        </div>

        {columnsError ? <div className="fields-error">{columnsError}</div> : null}

        <div className="fields-table-wrap" title="Only normalized name is editable">
          <table className="fields-table" aria-label="Schema columns">
            <thead>
              <tr>
                <th scope="col">Entity</th>
                <th scope="col">Schema</th>
                <th scope="col">Created</th>
                <th scope="col">Original column</th>
                <th scope="col">
                  Normalized column <span className="fields-th-hint">(editable)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {columnsLoading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={`s-${idx}`} className="fields-skeleton-row">
                    <td>
                      <span className="fields-skeleton-line" />
                    </td>
                    <td>
                      <span className="fields-skeleton-line fields-skeleton-line--wide" />
                    </td>
                    <td>
                      <span className="fields-skeleton-line" />
                    </td>
                    <td>
                      <span className="fields-skeleton-line fields-skeleton-line--wide" />
                    </td>
                    <td>
                      <span className="fields-skeleton-line fields-skeleton-line--wide" />
                    </td>
                  </tr>
                ))
              ) : columns.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="fields-empty">
                      {schemaId ? (
                        <>
                          <div className="fields-empty__title">No columns found</div>
                          <div className="fields-empty__subtitle">
                            Try “Fetch Latest” to sync columns from the schema definition.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="fields-empty__title">Select a schema</div>
                          <div className="fields-empty__subtitle">
                            Choose a schema above to view and edit its normalized field names.
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                columns.map((c) => (
                  <tr key={c.schema_col_id}>
                    <td className="fields-mono">{c.entity_name || '—'}</td>
                    <td>{c.schema_name || '—'}</td>
                    <td className="fields-created-at fields-mono">
                      {c.created_at ? formatDateTime(c.created_at) : '—'}
                    </td>
                    <td className="fields-mono">{c.original_column_name}</td>
                    <td>
                      <InlineEditableCell
                        value={c.normalized_column_name}
                        placeholder="Enter normalized name"
                        tooltip="Only normalized name is editable"
                        disabled={!schemaId || !!savingById[c.schema_col_id]}
                        saving={!!savingById[c.schema_col_id]}
                        onCommit={async (next) => {
                          const id = c.schema_col_id;
                          const prev = c.normalized_column_name;
                          // Optimistic update
                          setColumns((cur) =>
                            cur.map((row) =>
                              row.schema_col_id === id
                                ? { ...row, normalized_column_name: next }
                                : row
                            )
                          );
                          setSavingById((m) => ({ ...m, [id]: true }));
                          try {
                            await schemaColumnsService.updateNormalized(id, next);
                            showSuccess('Updated');
                          } catch (e) {
                            // Revert on error
                            setColumns((cur) =>
                              cur.map((row) =>
                                row.schema_col_id === id
                                  ? { ...row, normalized_column_name: prev }
                                  : row
                              )
                            );
                            showError(e.message || 'Failed to update');
                          } finally {
                            setSavingById((m) => {
                              const copy = { ...m };
                              delete copy[id];
                              return copy;
                            });
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UploadSchemaColumnsModal
        open={uploadOpen}
        schemaName={schemaLabel}
        disabled={!schemaId || uploading}
        loading={uploading}
        summary={uploadSummary}
        onClose={() => setUploadOpen(false)}
        onUpload={async (file) => {
          if (!schemaId) return;
          try {
            setUploading(true);
            setUploadSummary(null);
            const res = await schemaColumnsService.importFile(file);
            const summary = {
              success: !!res?.success,
              updated: Number(res?.updated || 0),
              skipped: Number(res?.skipped || 0),
              errors: Array.isArray(res?.errors) ? res.errors : []
            };
            setUploadSummary(summary);
            if (summary.success) {
              showSuccess(`Upload processed (updated ${summary.updated}, skipped ${summary.skipped})`);
              await loadColumns(schemaId);
              const isAllSuccess = summary.skipped === 0 && summary.errors.length === 0;
              if (isAllSuccess) {
                setUploadOpen(false);
              }
            } else {
              showError('Upload failed');
            }
          } catch (e) {
            showError(e.message || 'Upload failed');
          } finally {
            setUploading(false);
          }
        }}
      />
    </div>
  );
};

const AdminFields = () => {
  return (
    <SidebarLayout>
      <AdminFieldsPanel />
    </SidebarLayout>
  );
};

export default AdminFields;

